import React from "react";

import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { toast } from "react-toastify";

import banner from "assets/tmsnewpsd.png";
import { setUser } from "store/authSlice";
import { useLoginMutation } from "store/services/baseApi";
import { useRegisterUserMutation } from "store/services/userApi";

import { useAppMeta } from "hooks";
import SideBannerLayout from "layouts/SideBannerLayout";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const INITIAL_STATE = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  category: "Organization",
};

const RegisterScreen = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { appMeta } = useAppMeta();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
    },
  });

  // RTK Query
  const [registerUser, { isLoading: registeringUser }] =
    useRegisterUserMutation();

  const [loginUser, { isLoading: loggingUser }] = useLoginMutation();

  // methods
  const handleRegister = async (data) => {
    try {
      await registerUser(data).unwrap();
      await loginUser(data)
        .unwrap()
        .then((res) => {
          dispatch(setUser(res));
          navigate("/app/crm/dashboard");
        })
        .catch((err) =>
          toast.error(err?.data?.message ?? "Couldn't login user")
        );
      reset({ ...INITIAL_STATE });
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't register user");
    }
  };

  return (
    <SideBannerLayout banner={banner}>
      {/* <img src={Logo} alt="Logo" className="h-20 object-cover" /> */}
      <img src={appMeta.logoSmall} alt={appMeta.title} className="mb-2 px-3" />

      <div className="p-2 px-3">
        <div className="text-gray mb-10 text-center">
          Register now to manage your fleet, track trips, and optimize logistics
          with ease.
        </div>
        <div className="flex flex-col space-y-8">
          <div className="mb-2">
            <Controller
              name="name"
              control={control}
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Full Name"
                  placeholder="Enter your full name"
                  size="small"
                  type="text"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
            {errors.name && <p className="error">{errors.name.message}</p>}
          </div>
          <div className="mb-2">
            <Controller
              name="email"
              control={control}
              rules={{ required: "Email is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Email Address"
                  placeholder="Enter your email address"
                  size="small"
                  type="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            {errors.email && <p className="error">{errors.email.message}</p>}
          </div>
          <div className="mb-2">
            <Controller
              name="password"
              control={control}
              rules={{ required: "Provide a password" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Password"
                  placeholder="Enter a password"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    shrink: true,
                  }}
                />
              )}
            />
            {errors.password && (
              <p className="error">{errors.password.message}</p>
            )}
          </div>
          <div className="mb-2">
            <Controller
              name="confirmPassword"
              control={control}
              rules={{ required: "Please confirm your password" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Confirm Password"
                  placeholder="Confirm your password"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    shrink: true,
                  }}
                />
              )}
            />
            {errors.confirmPassword && (
              <p className="error">{errors.confirmPassword.message}</p>
            )}
          </div>
          <div className="flex items-baseline justify-between">
            <div className="flex space-x-4">
              <Button
                variant="contained"
                disabled={registeringUser || loggingUser}
                onClick={handleSubmit((data) => handleRegister(data))}
              >
                Register
              </Button>
              <Link to="/sign-in" disabled={registeringUser || loggingUser}>
                <Button className="w-full bg-blue-200 text-blue-800 hover:bg-blue-300">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SideBannerLayout>
  );
};

export default RegisterScreen;
